import { useExtensionsDevtools, useModuleFederationDevtools } from '@interhyp/platform-devtools';
import { effectExtension, EffectProvider } from '@interhyp/platform-effect';
import { ExtensionProvider } from '@interhyp/platform-plugin';
export { ExtensionProvider as PluginProvider, createExtensionPoint as createPlugin } from '@interhyp/platform-plugin';
import { createContext, useMemo, useState, useCallback, useEffect, useContext, useRef } from 'react';
import { modalExtension } from '@interhyp/platform-modal';
import { routeExtension } from '@interhyp/platform-router';
export { useFilteredRoutes, useContextRoutes as useRouteConfigurations } from '@interhyp/platform-router';
import { jsx, Fragment } from 'react/jsx-runtime';
import { getAPM } from '@interhyp/platform-apm';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { getAPIPath } from '@interhyp/platform-request';

function createModule(config) {
  return config;
}

const moduleContext = /*#__PURE__*/createContext({
  modules: [],
  addModules: () => void 0
});

function useCollectedExtensions(modules) {
  return useMemo(() => {
    return modules.flatMap(module => {
      const extensions = [];
      if (module.plugins) {
        extensions.push(...module.plugins);
      }
      if (module.extensions) {
        extensions.push(...module.extensions);
      }
      if (module.modals) {
        extensions.push(...module.modals.map(modalConfig => modalExtension.register(modalConfig)));
      }
      if (module.routes) {
        extensions.push(...module.routes.map(routeConfig => routeExtension.register(routeConfig)));
      }
      if (module.hooks) {
        extensions.push(...module.hooks.map(hookConfig => effectExtension.register(hookConfig)));
      }
      return {
        moduleName: module.name,
        extensions
      };
    });
  }, [modules]);
}

function ModuleProvider({
  children,
  modules: initialModules,
  toggles
}) {
  const [modules, setModules] = useState(initialModules ?? []);
  const collectedExtensions = useCollectedExtensions(modules);
  const flattedExtensions = useMemo(() => collectedExtensions.flatMap(({
    extensions
  }) => extensions), [collectedExtensions]);
  const addModules = useCallback(modules => {
    setModules(currentModules => {
      const moduleSet = new Set(currentModules);
      modules.forEach(module => {
        moduleSet.add(module);
      });
      return [...moduleSet];
    });
  }, []);
  const value = useMemo(() => {
    return {
      modules,
      addModules
    };
  }, [addModules, modules]);
  const {
    setCollectedExtensions,
    setToggles
  } = useExtensionsDevtools();
  useEffect(() => {
    setCollectedExtensions(collectedExtensions);
    setToggles(toggles ?? []);
  }, [collectedExtensions, toggles, setCollectedExtensions, setToggles]);
  return /*#__PURE__*/jsx(moduleContext.Provider, {
    value: value,
    children: /*#__PURE__*/jsx(ExtensionProvider, {
      toggles: toggles,
      extensions: flattedExtensions,
      children: /*#__PURE__*/jsx(EffectProvider, {
        children: children
      })
    })
  });
}

function useModules() {
  return useContext(moduleContext);
}

function promiseIsFulfilled(promise) {
  return promise.status === 'fulfilled';
}

/**
 * Remove rejected and return values of fulfilled promises
 * @param settledPromises
 */
function getSettledPromiseValues(settledPromises) {
  return settledPromises.filter(promiseIsFulfilled).map(promise => promise.value);
}

/**
 * Execute the given sideEffect for each group of promises when they were resolved.
 * Return a single promise which groups all promises and their sideEffects.
 * @param promises
 * @param sideEffect
 */
async function executePromiseSideEffects(promises, sideEffect) {
  // Call side effect for each group of promises
  const promisesAfterSideEffect = promises.map(async promise => {
    const promisesData = getSettledPromiseValues(await Promise.allSettled(promise));
    await sideEffect(promisesData);
    return promisesData;
  });

  // Ignore rejected promises
  return getSettledPromiseValues(await Promise.allSettled(promisesAfterSideEffect));
}

function isModuleDefined(module) {
  return typeof module !== 'undefined';
}

function applyOverride(remote) {
  const sessionOverride = sessionStorage.getItem(remote.name);
  const localOverride = localStorage.getItem(remote.name);
  const override = sessionOverride ?? localOverride;
  if (override) {
    remote.source = override;
  }
  return remote;
}

async function initializeWebpackModule(name, specifier, transaction) {
  const apm = getAPM();
  const span = transaction?.startSpan('initializeWebpackModule');
  try {
    await __webpack_init_sharing__('default');
    const container = window[name];
    if (!container) {
      throw new Error(`Container "${name}" not present (specifier: ${specifier})`);
    }
    await container.init(__webpack_share_scopes__.default);
    const factory = await container.get(specifier);
    const esModule = await factory();
    return esModule.default;
  } catch (error) {
    apm.captureError(error);
    console.error(`Could not load ${name}${specifier} because of the following error`);
    console.error(error);
  } finally {
    span?.end();
  }
}

function initializeWebpackModules(remote, blocker, transaction) {
  return remote.imports.map(async specifier => {
    if (blocker) {
      await blocker;
    }
    return initializeWebpackModule(remote.name, specifier, transaction);
  });
}

/**
 * Wait maximum of x seconds to settle the given promises.
 * Stop the sleep timer if all promises were settled before the timeout.
 * @param promisesToWaitFor
 * @param waitTimeoutInMs
 */
async function limitWaitForPromises(promisesToWaitFor, waitTimeoutInMs) {
  // create a timeout for x milliseconds and wait this time to catch the scripts
  // if there are scripts which are not fetched inside this time range, they will be loaded afterwards
  const {
    promise: sleepPromise,
    cancel: cancelSleep
  } = sleep(waitTimeoutInMs);

  // all settled should stop the sleep
  // eslint-disable-next-line promise/catch-or-return
  Promise.allSettled(promisesToWaitFor).then(cancelSleep);
  await sleepPromise;
}
function sleep(milliseconds) {
  let timeout;
  let resolveRef;
  const promise = new Promise(resolve => {
    timeout = window.setTimeout(resolve, milliseconds);
    resolveRef = resolve;
  });
  const cancel = () => {
    window.clearTimeout(timeout);
    resolveRef();
  };
  return {
    promise,
    cancel
  };
}

const cache = new Map();

/**
 * Adds the remoteEntry.js (or other entry file) to the document head inside a script tag
 * @param src
 * @param transaction
 * @param reAddTimeout
 */
function loadEntryScript(src, transaction, reAddTimeout = 5000) {
  const apm = getAPM();
  if (cache.has(src)) {
    return cache.get(src);
  }
  const span = transaction?.startSpan('loadEntryScript');
  const promise = new Promise((resolve, reject) => {
    try {
      const onFinalError = evt => {
        apm.captureError(`Could not load remote ${src} (finally). Error:\n${evt.message}`);
        reject(evt.message);
      };
      const retryCreateDomScript = () => {
        const retryTransaction = apm.startTransaction('createSecondScript', 'custom', {
          managed: true
        });
        createDomScript(src, resolve, onFinalError, () => {
          retryTransaction?.end();
        });
      };
      let initialScriptLoaded = false;
      let timeout;
      const initialScript = createDomScript(src, () => {
        initialScriptLoaded = true;
        clearTimeout(timeout);
        resolve();
      }, (evt, oldScript) => {
        oldScript.remove();
        apm.captureError(`Could not load remote ${src}. Retry loading script. Error:\n${evt.message}`);
        // Create second script if initial script runs into an error
        retryCreateDomScript();
      });

      // Retry adding script after a timeout
      timeout = window.setTimeout(() => {
        if (initialScriptLoaded) {
          return;
        }
        initialScript.remove();
        // Create second script if initial script hasn't been loaded after given re-add timeout
        retryCreateDomScript();
      }, reAddTimeout);
    } catch (error) {
      apm.captureError(`Could not load remote ${src}. Error:\n${typeof error === 'object' ? JSON.stringify(error) : error}`);
      console.error(`Could not load remote ${src}`, error);
      reject(error);
    }
  });

  // eslint-disable-next-line promise/prefer-await-to-then,promise/catch-or-return
  promise.finally(() => {
    span?.end();
  });
  cache.set(src, promise);
  return promise;
}
function createDomScript(src, onLoad, onError, onFinish) {
  const cacheBuster = Date.now();
  const script = document.createElement('script');
  script.src = `${src}?t=${cacheBuster}`;
  script.type = 'text/javascript';
  script.async = true;
  script.addEventListener('load', () => {
    onFinish?.();
    onLoad();
  });
  script.addEventListener('error', evt => {
    onFinish?.();
    onError(evt, script);
  });
  document.head.append(script);
  return script;
}

async function loadRemotes(remotes, transaction, remoteTimeoutInMs = 10_000) {
  const loadedScriptPromises = remotes.map(applyOverride).map(({
    source
  }) => {
    return createPromiseWithStatus(loadEntryScript(source, transaction));
  });

  // if there are scripts which are not fetched inside the given time range, they will be loaded afterwards
  await limitWaitForPromises(loadedScriptPromises, remoteTimeoutInMs);
  const loadedModulePromises = [];

  // load module for each script which is resolved
  loadedScriptPromises.forEach((scriptPromise, index) => {
    const remote = remotes[index];
    const modulePromises = initializeWebpackModules(remote, scriptPromise.status === 'resolved' ? undefined : scriptPromise, transaction);
    loadedModulePromises.push(modulePromises);
  });
  return loadedModulePromises;
}
function createPromiseWithStatus(promise) {
  const statusPromise = Object.assign(promise, {
    status: 'pending'
  });
  promise
  // eslint-disable-next-line promise/prefer-await-to-then
  .then(() => {
    statusPromise.status = 'resolved';
  })
  // eslint-disable-next-line promise/prefer-await-to-then
  .catch(() => {
    statusPromise.status = 'rejected';
  });
  return statusPromise;
}

/**
 * Helper hook for loading given list of remotes
 * @param remotes
 * @param transaction
 * @param remoteTimeoutInMs
 */
function useLoadRemotes(remotes, transaction, remoteTimeoutInMs) {
  const loadedRemotes = useRef([]);
  const {
    addModules
  } = useModules();
  const {
    addRemotes
  } = useModuleFederationDevtools();
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState();
  useEffect(() => {
    // skip loading if there are no remotes at all
    if (!remotes) {
      return;
    }

    // skip loading if there is an empty list and also set status to success
    if (remotes.length === 0) {
      setStatus('success');
      return;
    }

    // Check if given remotes has been already loaded
    const filteredRemotes = remotes.filter(r => !loadedRemotes.current.some(loadedRemote => loadedRemote.name === r.name && loadedRemote.source === r.source));

    // skip loading if there are no remotes to load
    if (filteredRemotes.length === 0) {
      return;
    }
    loadedRemotes.current = [...loadedRemotes.current, ...filteredRemotes];
    async function resolveRemotes() {
      try {
        const modulePromises = await loadRemotes(filteredRemotes, transaction, remoteTimeoutInMs);

        // Add each group of modules after it was fully loaded
        const moduleConfigs = await executePromiseSideEffects(modulePromises, groupedModules => {
          const validModules = groupedModules.filter(isModuleDefined);
          if (validModules.length > 0) {
            addModules(validModules);

            // Set the status to parital success after the first module was successful added
            setStatus('partialSuccess');
          }
        });

        // Add remotes and set status to success after all modules are successful added
        addRemotes({
          remotes: filteredRemotes,
          modules: moduleConfigs
        });
        setStatus('success');
      } catch (error) {
        setError(error);
        setStatus('error');
        console.error(error);
      } finally {
        transaction?.end();
      }
    }
    void resolveRemotes();

    // Do not add addRemotes and loadedRemotes here to prevent multiple executions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addModules, remotes, transaction, remoteTimeoutInMs]);
  return {
    status,
    error
  };
}

function RemoteProvider({
  remote,
  remotes,
  children,
  fallback,
  lazy,
  onStatusChange,
  onError
}) {
  // Replace type cast with isDefined util
  const concatenatedRemotes = useMemo(() => [remote, ...(remotes ?? [])].filter(Boolean), [remote, remotes]);
  const {
    status,
    error
  } = useLoadRemotes(concatenatedRemotes);
  const isFullyLoaded = status === 'success';
  useEffect(() => {
    onStatusChange?.(status);
  }, [onStatusChange, status]);
  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [onError, error]);

  // Generate fallback content if it's not fully loaded
  const fallbackContent = useMemo(() => {
    if (!isFullyLoaded && !lazy) {
      return typeof fallback === 'function' ? fallback({
        status,
        error
      }) : fallback;
    }
  }, [fallback, status, error, isFullyLoaded, lazy]);
  if (fallbackContent) {
    return fallbackContent;
  }
  return /*#__PURE__*/jsx(Fragment, {
    children: children
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchInterval: false
    }
  }
});

/**
 * Fetch authorization for actions
 * @param authzActionKeys
 */
async function fetchActiveToggleAuthorizations(authzActionKeys) {
  const apm = getAPM();
  if (authzActionKeys.length === 0) {
    return [];
  }
  const data = {
    permissions: authzActionKeys.map(action => ({
      permissionId: action
    }))
  };
  try {
    const response = await fetch('/api/authorization/v1/permission/toggles', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      return [];
    }
    const {
      permissions: authzEntries
    } = await response.json();
    return authzEntries.filter(entry => entry.authz).map(entry => entry.permissionId);
  } catch (error) {
    apm.captureError(`TOGGLE_PROVIDER permission/toggles call failed - ${error}`);
    return [];
  }
}

/**
 * Fetch toggle configs and check which of them are active
 * @param configDefinitionKeys
 * @param userId
 * @param companyId
 */
async function fetchActiveToggleConfigs(configDefinitionKeys, userId, companyId) {
  const apm = getAPM();
  if (configDefinitionKeys.length === 0) {
    return [];
  }
  const searchParams = new URLSearchParams();
  searchParams.append('inherit', 'ALL');
  searchParams.append('entity-id', userId);
  searchParams.append('entity-type', 'USER');
  configDefinitionKeys.forEach(key => {
    searchParams.append('configuration-definition-keys', key);
  });
  try {
    const response = await fetch(`/api/configuration/companies/${companyId}/configuration-entries?${searchParams.toString()}`);
    if (!response.ok) {
      return [];
    }
    const configEntries = await response.json();
    return configEntries.filter(entry => {
      return entry.valueType === 'STRING' && entry.value === 'true';
    }).map(entry => entry.key);
  } catch (error) {
    apm.captureError(`TOGGLE_PROVIDER config call failed - ${error}`);
    return [];
  }
}

/**
 * The toggle provider fetches for the given configs and auth-actions their entries and converts them into toggle-keys which can be used for the plugin system
 * @param children
 * @param identity
 * @param configDefinitionKeys
 * @param authzActionKeys
 * @constructor
 */
function ToggleProvider({
  children,
  identity,
  configDefinitionKeys,
  authzActionKeys
}) {
  const {
    data: toggles
  } = useQuery({
    queryKey: ['fetch-toggles', identity, configDefinitionKeys, authzActionKeys],
    queryFn: async () => {
      if (!identity) {
        return [];
      }
      return [...(await fetchActiveToggleConfigs(configDefinitionKeys, identity.userId, identity.companyId)), ...(await fetchActiveToggleAuthorizations(authzActionKeys))];
    }
  }, queryClient);
  if (!identity) {
    return children([]);
  }
  if (!toggles) {
    return null;
  }
  return children(toggles ?? []);
}

function useModalConfigurations() {
  const {
    modules
  } = useModules();
  return useMemo(() => {
    return modules.flatMap(module => {
      return module.modals ?? [];
    });
  }, [modules]);
}

/**
 * Fetches remotes list and calls loading function
 * @param remoteTimeoutInMs
 * @param fetchPath
 * @param transactionName
 */
function useRemotes(remoteTimeoutInMs, fetchPath = `${getAPIPath('platform-frontend-modules')}/v1/remotes`, transactionName = 'useRemotes') {
  const {
    addModules
  } = useModules();
  const [remotesData, setRemotesData] = useState({});
  const [error, setError] = useState();
  useEffect(() => {
    async function resolveRemotes() {
      const apm = getAPM();
      const transaction = apm.startTransaction(transactionName, 'custom');
      try {
        const request = await fetch(fetchPath);
        if (request.status !== 200) {
          const errorMessage = await request.text();
          throw new Error(errorMessage);
        }
        const response = await request.json();
        setRemotesData({
          remotes: response.remotes,
          transaction
        });
      } catch (error) {
        setError(error);
        console.error(error);
      }
    }
    void resolveRemotes();
  }, [addModules, transactionName, fetchPath]);
  const remotesResponse = useLoadRemotes(remotesData.remotes, remotesData.transaction, remoteTimeoutInMs);
  if (error) {
    return {
      status: 'error',
      error
    };
  }
  return remotesResponse;
}

/**
 * Fetches public remotes list
 * (!) This hook was designed for iframe implementation and the slug (url) beeing fixed
 * (!) Changing the slug may lead into duplicate module loading
 * @param slug
 * @param remoteTimeoutInMs
 */
function usePublicRemotes(slug, remoteTimeoutInMs) {
  return useRemotes(remoteTimeoutInMs, `${getAPIPath('platform-frontend-modules')}/v1/public/remotes?slug=${slug}`, 'usePublicRemotes');
}

export { ModuleProvider, RemoteProvider, ToggleProvider, createModule, useModalConfigurations, useModules, usePublicRemotes, useRemotes };
//# sourceMappingURL=index.js.map
